// Environment configurations for ThermalX frontend
// Supports development, staging (Azure Dev), and production (Wieland)
// In production builds, development URLs are excluded

export interface EnvironmentConfig {
  name: string;
  baseUrl: string;
  apiBaseUrl: string;
  basename: string;
  cookieDomain?: string;
  forwardedHost: string;
  forwardedProto: string;
  proxyTarget?: string;
}

export const ENVIRONMENTS: Record<string, EnvironmentConfig> = {
  ...(process.env.NODE_ENV === 'development' && {
    development: {
      name: 'development',
      baseUrl: 'http://localhost:3000',
      apiBaseUrl: 'http://localhost:8000/',
      basename: '',
      cookieDomain: undefined,
      forwardedHost: 'localhost:3000',
      forwardedProto: 'http',
      proxyTarget: 'http://localhost:8000'
    }
  }),
  
  staging: {
    name: 'staging',
    baseUrl: 'https://devapimgmtthermalxeuw01.azure-api.net/devthermalx02app',
    apiBaseUrl: 'https://devapimgmtthermalxeuw01.azure-api.net/devthermalx02app/api/',
    basename: '/devthermalx02app',
    cookieDomain: '.azure-api.net',
    forwardedHost: 'devapimgmtthermalxeuw01.azure-api.net',
    forwardedProto: 'https',
    proxyTarget: 'https://devapimgmtthermalxeuw01.azure-api.net/devthermalx02app'
  },
  
  production: {
    name: 'production',
    baseUrl: 'https://thermalx.wieland.com',
    apiBaseUrl: 'https://thermalx.wieland.com/api/',
    basename: '',
    cookieDomain: '.wieland.com',
    forwardedHost: 'thermalx.wieland.com',
    forwardedProto: 'https',
    proxyTarget: 'https://thermalx.wieland.com'
  }
};

// Environment detection logic
export const detectEnvironment = (): string => {
  // Check for explicit environment variable first
  if (process.env.REACT_APP_ENV) {
    const env = process.env.REACT_APP_ENV.trim(); // Remove trailing spaces
    return env;
  }

  // In production builds, skip development logic
  if (process.env.NODE_ENV === 'production') {
    // For production builds, check if window is available for hostname detection
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const pathname = window.location.pathname;
      
      // Check for Azure dev instance - either by hostname or pathname
      if (hostname.includes('devapimgmtthermalxeuw01.azure-api.net') || 
          pathname.includes('/devthermalx02app')) {
        return 'staging';
      }
      
      if (hostname.includes('thermalx.wieland.com')) {
        return 'production';
      }
    }
    
    // Default to staging for unknown production environments
    return 'staging';
  }

  // Development environment logic
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }

  // Fallback
  return 'staging';
};

// Get current environment configuration
export const getCurrentEnvironment = (): EnvironmentConfig => {
  const envKey = detectEnvironment();
  return ENVIRONMENTS[envKey] || ENVIRONMENTS.development;
};

// Helper function to get full URL with basename
export const getFullUrl = (path: string = ''): string => {
  const env = getCurrentEnvironment();
  return `${env.baseUrl}${path}`;
};

// Helper function to get share URL
export const getShareUrl = (path: string, queryParams?: Record<string, string>): string => {
  const env = getCurrentEnvironment();
  const url = new URL(path, env.baseUrl);
  
  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }
  
  return url.toString();
}; 